import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  cancelInspectionCampaign,
  fetchInspectionCampaign,
  resumeInspectionCampaign,
  type InspectionCampaignView,
} from '../api/inspectionCampaigns';
import { ApiError, OfflineError } from '../api/http';
import { ca } from '../i18n/ca';
import { useAuth } from './AuthContext';
import { Shell } from './Shell';
import { fmtMoment } from './dates';

const when = (iso: string | null) => (iso ? fmtMoment(iso) : '—');

/**
 * The campaign's own status page ({@code /proves/inspeccions/campanya/:id}):
 * every item, its state, its session and capture count, and its error when it
 * has one, refreshing every 15 s while the campaign runs (build item 4). An
 * admin can stop it («Atura la campanya», the ticker leaves it alone from the
 * next tick on) and start it again («Reprèn»).
 */
export function InspectionCampaignStatus() {
  const { id } = useParams<{ id: string }>();
  const t = ca.inspectionCampaignStatusPage;
  const { me } = useAuth();
  const isAdmin = me?.roles.includes('ADMIN') ?? false;
  const [campaign, setCampaign] = useState<InspectionCampaignView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    fetchInspectionCampaign(id)
      .then((c) => {
        setCampaign(c);
        setError(null);
      })
      .catch((e) =>
        setError(
          e instanceof ApiError ? e.code : e instanceof OfflineError ? 'offline' : String(e),
        ),
      );
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (campaign?.state !== 'running') return;
    const timer = setInterval(load, 15000);
    return () => clearInterval(timer);
  }, [campaign?.state, load]);

  const act = async (fn: (id: string) => Promise<InspectionCampaignView>) => {
    if (!id) return;
    setBusy(true);
    try {
      setCampaign(await fn(id));
      setError(null);
    } catch (e) {
      setError(e instanceof ApiError ? e.code : e instanceof OfflineError ? 'offline' : String(e));
    } finally {
      setBusy(false);
    }
  };

  if (!id) return null;

  return (
    <Shell
      title={t.title(id)}
      width="wide"
      chips={
        campaign ? (
          <span className={`chip ${campaign.state}`}>
            {t.states[campaign.state] ?? campaign.state}
          </span>
        ) : undefined
      }
      actions={
        isAdmin && campaign ? (
          campaign.state === 'running' ? (
            <button
              type="button"
              disabled={busy}
              onClick={() => void act(cancelInspectionCampaign)}
            >
              {t.stop}
            </button>
          ) : campaign.state === 'cancelled' ? (
            <button
              type="button"
              className="primary"
              disabled={busy}
              onClick={() => void act(resumeInspectionCampaign)}
            >
              {t.resume}
            </button>
          ) : undefined
        ) : undefined
      }
    >
      <p>
        <Link to="/proves/inspeccions">← {t.back}</Link>
        {' · '}
        <Link to={`/proves/inspeccions/campanya/${id}/revisio`}>{t.openCampaignReview}</Link>
      </p>
      {error && (
        <p className="error" role="alert">
          {ca.errors[error] ?? ca.errors.unknown}
        </p>
      )}
      {campaign && (
        <>
          <p className="muted small">{t.progress(campaign.currentItem, campaign.totalItems)}</p>
          <ul className="campaign-status-items">
            {campaign.items.map((item) => (
              <li key={item.index} className={item.index === campaign.currentItem ? 'current' : ''}>
                <strong>{item.recipeTitle}</strong>
                <span className="muted small">{item.worldClass}</span>
                <span className={`chip ${item.state}`}>
                  {t.itemStates[item.state] ?? item.state}
                </span>
                {item.uploadCount > 0 && (
                  <span className="chip small">{t.captures(item.uploadCount)}</span>
                )}
                {item.retryCount > 0 && (
                  <span className="chip small">{t.retried(item.retryCount)}</span>
                )}
                {item.sessionCode && (
                  <Link to={`/proves/inspeccio/${item.sessionCode}`} className="small">
                    {t.openReview}
                  </Link>
                )}
                {item.error && <span className="error small">{t.itemError(item.error)}</span>}
                {item.startedAt && <span className="muted small">{when(item.startedAt)}</span>}
              </li>
            ))}
          </ul>
        </>
      )}
    </Shell>
  );
}
